import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, Divider, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ReactHookFormSelect2 } from 'common/components/form/bootstrap';
import ShowErrors from 'common/components/ShowErrors';
import { getIsOwnerRole } from 'common/utils/helpers/session';
import yupResolver from 'common/utils/yupResolver';

import * as profileSelectors from '../../profile/selectors';
import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

import RenderAdditionalInputsPerSystem from './RenderAdditionalInputsPerSystem';

const FormContainer = ({
    errors: serverErrors,
    handleClickCloseForm,
    handleClickSave,
    initialValues,
    isFetchingItem,
    isNewRecord,
    isSaving,
    presetScope,
    systems,
}) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState(presetScope);
    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        setScope(presetScope);
    }, [presetScope]);

    const systemUid = watch('system_uniqueid');

    const systemOptions = systems.map((item) => ({
        label: item.name,
        value: item.id,
    }));
    const isOwner = getIsOwnerRole();
    const isDisabledFields = !isOwner && scope === 'COMPANY';

    return (
        <form>
            {isDisabledFields && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('Only editable from the owner account')}
                </Alert>
            )}
            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect2
                    control={control}
                    disabled
                    label={t('System')}
                    name="system_id"
                    options={[
                        {
                            label: '',
                            options: systemOptions,
                        },
                    ]}
                />
            </Box>

            <RenderAdditionalInputsPerSystem
                control={control}
                disabled={isFetchingItem || isDisabledFields}
                isNew={isNewRecord}
                setValue={setValue}
                systemUid={systemUid}
            />

            <Divider sx={{ mb: 2 }} />

            <ShowErrors errors={serverErrors} />

            <Grid container mt={2}>
                <Grid
                    size="grow"
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: { xs: 'center', md: 'right' },
                    }}
                >
                    <Button
                        disabled={isSaving}
                        onClick={handleClickCloseForm}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 2, md: 1 },
                            mr: { xs: 0, md: 2 },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        disabled={isSaving}
                        onClick={handleSubmit(handleClickSave)}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 1, md: 2 },
                            mb: { xs: 2, md: 0 },
                        }}
                        type="button"
                        variant="outlined"
                    >
                        {t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getSaveItemErrors,
    initialValues: selectors.getInitialValues,
    isFetchingItem: selectors.getIsFetchingItem,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSavingItem,
    presetScope: profileSelectors.getPresetScope,
    systems: selectors.getFetchSystemsData,
});

const mapDispatchToProps = (dispatch) => ({
    handleClickSave: (filterData, values) =>
        dispatch(actions.save(filterData, values)),
});

FormContainer.propTypes = {
    errors: PropTypes.array,
    handleClickCloseForm: PropTypes.func,
    handleClickSave: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingItem: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    presetScope: PropTypes.oneOf(['USER', 'COMPANY']),
    systems: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
